import React, { useMemo } from 'react'
import { Box, Card, CardContent, TextField } from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'

import { useAptConfig } from '../../../../hooks/useAptConfig'
import { usePremiums } from '../../../../hooks/usePremiums'
import { useUser } from '../../../../hooks/useUser'
import { useGuestCards } from '../../../../hooks/Outreach/useGuestCards'
import { GuestCardFormResponse } from '../../../../hooks/Outreach/useGuestCardForm'
import {
    GuestCard,
    GuestCardCustomQuestion,
    GuestCardRequiredFields,
} from '../../../../models/Outreach/types'
import {
    GUEST_CARD_REMOVABLE_FIELDS,
    GuestCardQuestionFieldKey,
    isGuestCardFieldRequired,
} from '../../../../models/Outreach/services'
import { GuestCardFormFields } from '../../Leads/ProspectsTab/GuestCardFormFields'
import { AddGuestCardQuestionSlot } from './AddGuestCardQuestionSlot'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        backgroundColor: '#f5f5f5',
        margin: theme.spacing(-3),
        padding: theme.spacing(3, 2),
        minHeight: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
    },
    formCard: {
        maxWidth: 640,
        width: '100%',
        boxShadow:
            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        borderRadius: 12,
    },
    cardHeader: {
        padding: theme.spacing(3),
        borderBottom: '1px solid #e5e7eb',
        backgroundColor: '#ffffff',
        borderTopLeftRadius: 12,
        borderTopRightRadius: 12,
    },
    headerField: {
        '& .MuiOutlinedInput-root': {
            fontSize: '1.75rem',
            fontWeight: 700,
            color: '#1f2937',
        },
        '& .MuiOutlinedInput-input': {
            padding: theme.spacing(1, 1.5),
        },
        marginBottom: theme.spacing(1),
    },
    bodyField: {
        '& .MuiOutlinedInput-root': {
            fontSize: '0.875rem',
            color: '#6b7280',
        },
        '& .MuiOutlinedInput-input': {
            padding: theme.spacing(1, 1.5),
        },
    },
    formContent: {
        padding: theme.spacing(3),
    },
}))

export interface GuestCardEditorCanvasProps {
    header: string
    body: string
    onHeaderChange: (value: string) => void
    onBodyChange: (value: string) => void
    includeFlags: Record<GuestCardQuestionFieldKey, boolean>
    onRemoveField: (field: GuestCardQuestionFieldKey) => void
    onRestoreField: (field: GuestCardQuestionFieldKey) => void
    requiredFields: GuestCardRequiredFields
    onToggleRequired: (
        field: GuestCardQuestionFieldKey,
        required: boolean,
    ) => void
    customQuestions: GuestCardCustomQuestion[]
    selectedQuestionIds: number[]
    requiredQuestionIds: Set<number>
    onAddCustomQuestion: (
        question: GuestCardCustomQuestion,
        required?: boolean,
    ) => void
    onRemoveCustomQuestion: (questionId: number) => void
    onToggleCustomRequired: (questionId: number, required: boolean) => void
}

export const GuestCardEditorCanvas: React.FC<GuestCardEditorCanvasProps> = ({
    header,
    body,
    onHeaderChange,
    onBodyChange,
    includeFlags,
    onRemoveField,
    onRestoreField,
    requiredFields,
    onToggleRequired,
    customQuestions,
    selectedQuestionIds,
    requiredQuestionIds,
    onAddCustomQuestion,
    onRemoveCustomQuestion,
    onToggleCustomRequired,
}) => {
    const classes = useStyles()
    const { workspaceUser } = useUser()
    const { unitConfigList } = useAptConfig({ unitConfigList: true })
    const { premiums } = usePremiums()
    const { defaultMoveInDate, defaultMoveOutDate } = useGuestCards()

    const apartmentName = workspaceUser?.active_workspace?.name || ''
    const apartmentId = workspaceUser?.active_workspace?.id ?? 0

    const removedDefaultFields = GUEST_CARD_REMOVABLE_FIELDS.filter(
        (field) => !includeFlags[field],
    )

    const guestCardFormData = useMemo<GuestCardFormResponse>(() => {
        const unitConfigs = (unitConfigList || [])
            .filter((config) => config.shows_on_schedule)
            .map((config) => ({
                ...config,
                bed_count:
                    (config as { bed_count?: number | null }).bed_count ?? null,
                bath_count:
                    (config as { bath_count?: number | null }).bath_count ??
                    null,
            }))

        const selected = new Set(selectedQuestionIds)
        const guestCard: GuestCard = {
            id: 0,
            card_name: '',
            archived: false,
            default: false,
            apartment: apartmentId,
            header,
            body,
            name: includeFlags.name,
            email: includeFlags.email,
            phone: includeFlags.phone,
            preferred_unit_config: includeFlags.preferred_unit_config,
            preferred_premiums: includeFlags.preferred_premiums,
            preferred_rent_cost: includeFlags.preferred_rent_cost,
            preferred_move_in_date: includeFlags.preferred_move_in_date,
            preferred_move_out_date: includeFlags.preferred_move_out_date,
            preferred_roommates: includeFlags.preferred_roommates,
            school_attendance: includeFlags.school_attendance,
            additional_requests: includeFlags.additional_requests,
            required_fields: requiredFields,
            default_move_in_date: defaultMoveInDate,
            default_move_out_date: defaultMoveOutDate,
            card_questions: customQuestions
                .filter((question) => selected.has(question.id))
                .map((question, idx) => ({
                    id: question.id,
                    custom_question: question,
                    order: selectedQuestionIds.indexOf(question.id) ?? idx,
                    required: requiredQuestionIds.has(question.id),
                    created_date: '',
                    modified_date: '',
                }))
                .sort((a, b) => a.order - b.order),
            created_date: '',
            modified_date: '',
        }

        return {
            guest_card: guestCard,
            name: null,
            email: null,
            phone: null,
            unit_configs: unitConfigs,
            premiums: (premiums || [])
                .filter((premium) => premium.is_active)
                .map((premium) => ({
                    id: premium.id,
                    name: premium.name,
                    description: premium.description,
                    amount: String(premium.amount),
                })),
            apartment_name: apartmentName,
        }
    }, [
        apartmentId,
        apartmentName,
        body,
        customQuestions,
        defaultMoveInDate,
        defaultMoveOutDate,
        header,
        includeFlags,
        premiums,
        requiredFields,
        requiredQuestionIds,
        selectedQuestionIds,
        unitConfigList,
    ])

    return (
        <Box className={classes.root}>
            <Card className={classes.formCard}>
                <Box className={classes.cardHeader}>
                    <TextField
                        className={classes.headerField}
                        fullWidth
                        variant="outlined"
                        value={header}
                        onChange={(e) => onHeaderChange(e.target.value)}
                        placeholder={apartmentName || 'Header'}
                    />
                    <TextField
                        className={classes.bodyField}
                        fullWidth
                        variant="outlined"
                        multiline
                        rows={2}
                        value={body}
                        onChange={(e) => onBodyChange(e.target.value)}
                        placeholder="Please fill out the form below"
                    />
                </Box>
                <CardContent className={classes.formContent}>
                    <GuestCardFormFields
                        guestCardFormData={guestCardFormData}
                        prospect={null}
                        onSubmit={() => undefined}
                        hideSubmit
                        editor={{
                            fieldRequired: (field) =>
                                isGuestCardFieldRequired(
                                    { required_fields: requiredFields },
                                    field,
                                ),
                            onRemoveField,
                            onToggleRequired,
                            onRemoveCustomQuestion,
                            onToggleCustomRequired,
                        }}
                    />
                    <AddGuestCardQuestionSlot
                        removedDefaultFields={removedDefaultFields}
                        onRestoreDefault={onRestoreField}
                        selectedQuestionIds={selectedQuestionIds}
                        onAddExistingQuestion={onAddCustomQuestion}
                    />
                </CardContent>
            </Card>
        </Box>
    )
}
